import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import FinalizeCard from './FinalizeCard';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { getSectionAnswers } from './answersUtil';
import { Report } from 'shared/components/WelcomeBack/utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { getSectionIcon } from 'shared/components/WelcomeBack/Sections';
import { BottomBar } from '../BottomBar';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import MakeEditsModal, {
  MakeEditsModalProps,
} from '../../../NPOFeedback/MakeEdits/MakeEditsModal';
import {
  ReportQuestion,
  getQuestionsFromAnswersArray,
  updateAnswerState,
} from './utils';
import { getFinalizeMarkerQuestionId } from './utils';
import {
  UpdateReportResponse,
  updateIndicatorAnswer,
} from '../ImpactIndicator/api';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  FeedbackQuestion,
  FeedbackQuestionsById,
} from '../../../NPOFeedback/MakeEdits/api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';

interface ReportFinalizeSummaryProps {
  report: Report;
  programName?: string;
  answers: Answer[];
  loadingAnswers: boolean;
  isInterimStage: boolean;
  questions: ReportQuestion[];
  reloadAnswers: () => unknown;
  handlePressOption: (sectionId: number, optionId: number) => unknown;
}

const StyledDiv = styled.div`
  margin-bottom: 160px;
  margin-top: ${SPACERS.extraLarge};
  .headings {
    max-width: 730px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    .subheading {
      margin-top: ${SPACERS.large};
      margin-bottom: ${SPACERS.large};
    }
  }
  .location {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${SPACERS.tiny};
    max-width: 250px;
  }
`;

const ReportFinalizeSummary: React.FC<ReportFinalizeSummaryProps> = ({
  report,
  answers,
  programName = '',
  questions: q,
  loadingAnswers,
  isInterimStage,
  reloadAnswers,
  handlePressOption,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<UpdateReportResponse>();
  const [selectedSection, setSelectedSection] = useState<
    MakeEditsModalProps['selectedSection'] | undefined
  >(undefined);
  const [questions, setQuestions] = useState<FeedbackQuestionsById | undefined>(
    undefined,
  );

  useEffect(() => {
    setQuestions(getQuestionsFromAnswersArray(q, answers, report));
  }, [q, answers]);

  const handleAnswerUpdate = useCallback(
    (
      questionId: number,
      answer: string,
      questionText: string,
      questionType: FeedbackQuestion['questionType'],
    ) => {
      setQuestions(q => {
        return updateAnswerState({
          answer: answer,
          questionId,
          questionText,
          questions: q,
          questionType,
        });
      });
    },
    [setQuestions],
  );

  useEffect(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      reloadAnswers();
      setSelectedSection(undefined);
      handlePressOption(isInterimStage ? 1 : 2, 1);
    }
  }, [
    requestState.type,
    reloadAnswers,
    setSelectedSection,
    handlePressOption,
    isInterimStage,
  ]);

  const handleSaveAnswer = useCallback(
    (questionId: number, answerId: number | undefined, answer: string) => {
      if (!client) return;
      makeRequest(
        updateIndicatorAnswer(
          client,
          answerId,
          true,
          questionId,
          report.dbId,
          answer,
        ),
      );
    },
    [client],
  );

  const handleClose = useCallback(() => {
    setSelectedSection(undefined);
  }, [setSelectedSection]);

  const handleDescription = useCallback(
    () =>
      setSelectedSection({
        question: {
          questionId: 1,
          questionText: questions?.[1]?.text || 'Unknown',
        },
        section: {
          label: 'Description',
          sectionId: 'description',
        },
      }),
    [questions, setSelectedSection],
  );

  const handleDates = useCallback(
    () =>
      setSelectedSection({
        question: {
          questionId: 10001,
          questionText: questions?.[10001]?.text || 'Unknown',
        },
        section: {
          label: 'Dates',
          sectionId: 'dates',
        },
      }),
    [questions, setSelectedSection],
  );

  const handleLocation = useCallback(
    () =>
      setSelectedSection({
        question: {
          questionId: 30017,
          questionText: questions?.[30017]?.text || 'Unknown',
        },
        section: {
          label: 'Dates',
          sectionId: 'dates',
        },
      }),
    [questions, setSelectedSection],
  );

  const handleActivities = useCallback(
    () =>
      setSelectedSection({
        question: {
          questionId: 110001,
          questionText: questions?.[110001]?.text || 'Unknown',
        },
        section: {
          label: 'Activities',
          sectionId: 'activities',
        },
      }),
    [questions, setSelectedSection],
  );

  const handleNext = useCallback(() => {
    const questionId = getFinalizeMarkerQuestionId(1, report.stage);
    if (!questionId) return;
    handleSaveAnswer(questionId, undefined, 'Completed');
  }, [handleSaveAnswer, report.stage]);

  if (!questions) {
    return <TrueImpactLoading />;
  }

  return (
    <>
      <StyledDiv>
        <div className="headings">
          <TrueImpactText variant="heading-page">
            Do you need to make any changes to the following sections of your
            report?
          </TrueImpactText>
          <TrueImpactText variant="body-large" className="subheading">
            If everything looks good, feel free to continue to the next section.
          </TrueImpactText>
        </div>
        <FinalizeCard
          icon={getSectionIcon('description')}
          title="Description"
          subheading="This represents the demographic categories of your program's beneficiaries."
          onClick={handleDescription}
        >
          {getSectionAnswers('description', answers)}
        </FinalizeCard>
        <FinalizeCard
          icon={getSectionIcon('dates')}
          title="Dates"
          subheading="This is the typical reporting time period you use for this program."
          onClick={handleDates}
        >
          {getSectionAnswers('dates', answers)}
        </FinalizeCard>
        <FinalizeCard
          icon={getSectionIcon('location')}
          title="Location"
          subheading={'This is where your program operates and the percentages of your program’s services and impacts that occur in ' +
            'each location.'}
          onClick={handleLocation}
        >
          {getSectionAnswers('location', answers)}
        </FinalizeCard>
        <FinalizeCard
          icon={getSectionIcon('activities')}
          title="Activities"
          subheading="These are the activities your program delivers."
          onClick={handleActivities}
        >
          {getSectionAnswers('activities', answers)}
        </FinalizeCard>
      </StyledDiv>
      <BottomBar
        disabled={false}
        hideBack={true}
        hideNext={false}
        handleBack={undefined}
        handleNext={handleNext}
        backTitle="Back"
        nextTitle="Save & Continue"
      />
      {selectedSection && (
        <MakeEditsModal
          programName={programName}
          isError={false}
          isFinalizedUI={true}
          isLoading={false}
          feedback={[]}
          report={report}
          feedbackByQuestionId={{}}
          isAnswersLoading={loadingAnswers}
          reloadAnswers={reloadAnswers}
          markAsCompleted={reloadAnswers}
          handleAnswerUpdate={handleAnswerUpdate}
          onClose={handleClose}
          open={true}
          questions={questions}
          reportId={report.dbId}
          reportYear={report.year}
          selectedSection={selectedSection}
        />
      )}
    </>
  );
};

export default ReportFinalizeSummary;
